import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { customFetch } from "@workspace/api-client-react";

interface ImageUploadProps {
  currentUrl?: string | null;
  onUpload: (url: string) => void;
  uploadEndpoint: string;
  label?: string;
  shape?: "circle" | "square";
  size?: "sm" | "md" | "lg";
  fallback?: React.ReactNode;
}

const SIZES = {
  sm: "w-16 h-16",
  md: "w-24 h-24",
  lg: "w-32 h-32",
};

export function ImageUpload({
  currentUrl,
  onUpload,
  uploadEndpoint,
  label = "Upload Image",
  shape = "circle",
  size = "md",
  fallback,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const displayUrl = preview || currentUrl;
  const sizeClass = SIZES[size];
  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-xl";

  async function handleFile(file: File) {
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 5MB", variant: "destructive" });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const data = await customFetch<{ url: string }>(`/api${uploadEndpoint}`, {
        method: "POST",
        body: formData,
      });
      onUpload(data.url);
      toast({ title: "Image uploaded" });
    } catch (err: unknown) {
      let title = "Upload failed";
      let message = "Something went wrong. Please try again.";
      if (err && typeof err === "object" && "status" in err) {
        const status = (err as { status: number }).status;
        if (status === 401 || status === 403) {
          title = "Authentication required";
          message = "Your session has expired. Please log in again.";
        } else if (status === 413) {
          title = "File too large";
          message = "Maximum file size is 5MB.";
        }
      }
      if (err instanceof Error && message === "Something went wrong. Please try again.") {
        message = err.message;
      }
      toast({ title, description: message, variant: "destructive" });
      setPreview(null);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-4">
      <div
        className={`${sizeClass} ${shapeClass} overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center relative cursor-pointer group`}
        onClick={() => fileRef.current?.click()}
      >
        {displayUrl ? (
          <>
            <img src={displayUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {uploading ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Upload className="w-5 h-5 text-white" />
              )}
            </div>
          </>
        ) : (
          <>
            {fallback || (
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5" />
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="border-white/10"
        >
          {uploading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {label}
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground">JPG, PNG, WebP · Max 5MB</p>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
